/**
 * HxCascadingSearch Component - Simplified Implementation
 *
 * A cascading dropdown component that uses nested option structure and leverages the existing HxDropdown component for
 * consistent UI and behavior.
 */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxDropdownComponent } from '../select/dropdown/dropdown.component';
import { HxOption } from '../select/shared/option.model';
import {
  CascadeOption,
  CascadePathSelection,
  CascadeSelection,
  CascadingSearchConfig,
  DropdownDisplayOption,
} from './cascading-search.types';

@Component({
  selector: 'hx-cascading-search',
  standalone: true,
  imports: [HxDropdownComponent],
  templateUrl: './cascading-search.html',
  styleUrls: ['./cascading-search.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxCascadingSearchComponent),
      multi: true,
    },
  ],
  host: {
    '[class]': 'hostClasses()',
  },
})
export class HxCascadingSearchComponent extends HxBaseControl {
  // ============================================================================
  // INPUTS
  // ============================================================================

  /** Configuration containing the cascade options and settings */
  readonly config = input.required<CascadingSearchConfig>();

  /** Placeholder text for the search input */
  readonly placeholder = input<string>('Search and select...');

  /** Enable search functionality within current level */
  readonly searchable = input<boolean>(true);

  /** Custom CSS classes for styling */
  readonly class = input<string>('');

  /** Whether the component is disabled */
  readonly disabled = input<boolean | undefined>(false);

  // ============================================================================
  // OUTPUTS
  // ============================================================================

  /** Emitted when the cascade selection changes */
  readonly cascadeUpdated = output<CascadeSelection>();

  /** Emitted when the cascade selection changes - simplified path-only version */
  readonly pathSelected = output<CascadePathSelection>();

  
  // ============================================================================
  // VIEW CHILDREN
  // ============================================================================

  /** Reference to the HxDropdown component */
  private readonly dropdownComponent = viewChild<HxDropdownComponent>('dropdown');

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  /** Current selection path from root to current level */
  readonly selectionPath = signal<CascadeOption[]>([]);

  /** Current search query */
  private readonly searchQuery = signal<string>('');

  /** Whether a final selection has been made */
  readonly isFinalSelection = signal<boolean>(false);

  /** Final display value when selection is complete */
  private readonly finalDisplayValue = signal<string>('');

  /** Final display subtitle when selection is complete */
  private readonly finalDisplaySubtitle = signal<string>('');

  /** Whether the dropdown is currently open */
  readonly isDropdownOpen = signal<boolean>(false);

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /** Flatten all options recursively with their full paths for deep search */
  private readonly flattenedOptionsWithPaths = computed(() => {
    const config = this.config();
    const results: { option: CascadeOption; path: CascadeOption[] }[] = [];

    const flatten = (options: CascadeOption[], currentPath: CascadeOption[]) => {
      options.forEach((option) => {
        const optionPath = [...currentPath, option];
        results.push({ option, path: optionPath });

        if (option.children && option.children.length > 0) {
          flatten(option.children, optionPath);
        }
      });
    };

    flatten(config.options, []);
    return results;
  });

  /** Whether we're in search mode (active search query) */
  private readonly isSearchMode = computed(() => {
    return this.searchQuery().trim().length > 0;
  });

  /** Search results filtered from all levels */
  private readonly searchResults = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return [];

    return this.flattenedOptionsWithPaths().filter(
      (item) =>
        item.option.label.toLowerCase().includes(query) ||
        (item.option.subtitle && item.option.subtitle.toLowerCase().includes(query))
    );
  });

  /** Current options to display in dropdown based on selection path */
  readonly currentLevelOptions = computed(() => {
    const path = this.selectionPath();
    const config = this.config();

    if (path.length === 0) {
      // Show root level options
      return config.options;
    }

    // Show children of the last selected option
    const lastSelected = path[path.length - 1];
    return lastSelected?.children || [];
  });

  /** Breadcrumb path for navigation (excludes current level) */
  readonly breadcrumbPath = computed(() => {
    const path = this.selectionPath();
    return path.length > 0 ? path.slice(0, -1) : [];
  });

  /** Options formatted for HxDropdown display with clickable path as first option */
  readonly dropdownOptions = computed((): HxOption<DropdownDisplayOption>[] => {
    // If searching, show search results from all levels
    if (this.isSearchMode()) {
      const searchResults = this.searchResults();
      const separator = this.config().pathSeparator || ' > ';

      return searchResults.map((result) => {
        const pathText = result.path.map((p) => p.label).join(separator);
        const pathSubtitle = result.path
          .map((p) => p.subtitle || '')
          .filter(Boolean)
          .join(separator);
        const hasChildren = result.option.children && result.option.children.length > 0;

        return {
          id: `search-${result.option.id}`,
          label: pathText + (hasChildren ? '>' : ''),
          subtitle: pathSubtitle || result.option.subtitle,
          value: {
            ...result.option,
            isSearchResult: true,
            searchResultPath: result.path,
          } as DropdownDisplayOption,
          disabled: result.option.disabled,
        };
      });
    }

    // Normal mode: show current level options with path navigation
    const currentOptions = this.currentLevelOptions();
    const path = this.selectionPath();
    const options: HxOption<DropdownDisplayOption>[] = [];

    // Add clickable path as first option if we have a path
    if (path.length > 0) {
      // Build path text with arrow separators: "US > California > Los Angeles"
      const separator = this.config().pathSeparator || ' > ';
      const pathText = path.map((item) => item.label).join(separator);
      const pathSubtitle = path
        .map((item) => item.subtitle || '')
        .filter(Boolean)
        .join(separator);

      // Add the path as a clickable option - each segment is part of the label
      // The option value contains navigation info for each segment
      options.push({
        id: 'path-navigation',
        label: pathText,
        subtitle: pathSubtitle,
        value: {
          id: 'path-navigation',
          label: pathText,
          value: path,
          isBreadcrumb: true,
          navigationLevel: path.length - 1,
        },
        className: 'hx-cascading-search__path-option',
      });

      // Add separator if we have child options
      if (currentOptions.length > 0) {
        options.push({
          id: 'separator',
          label: '',
          title: '',
          value: null,
          disabled: true,
          className: 'hx-cascading-search__separator',
        });
      }
    }

    // Add current level options
    currentOptions.forEach((option) => {
      const hasChildren = option.children && option.children.length > 0;
      options.push({
        id: option.id,
        label: option.label + (hasChildren ? ' > ' : ''),
        subtitle: option.subtitle,
        value: option,
        disabled: option.disabled,
      });
    });

    return options;
  });

  /** Current value to pass to HxDropdown - represents the path option for display */
  readonly currentDropdownValue = computed((): DropdownDisplayOption | null => {
    const path = this.selectionPath();

    if (path.length === 0) {
      return null;
    }

    // Return the path option value that matches our path-navigation option
    const separator = this.config().pathSeparator || ' > ';
    const pathText = path.map((item) => item.label).join(separator);
    const pathSubtitle = path
      .map((item) => item.subtitle || '')
      .filter(Boolean)
      .join(separator);
    return {
      id: 'path-navigation',
      label: pathText,
      subtitle: pathSubtitle,
      value: path,
      isBreadcrumb: true,
      navigationLevel: path.length - 1,
    };
  });

  /** Display value showing current path - only shown when dropdown is closed */
  readonly displayValue = computed(() => {
    // Only show the path value in dropdown trigger when dropdown is closed
    if (!this.isFinalSelection()) {
      return '';
    }

    return this.finalDisplayValue();
  });

  /** Display subtitle showing current path subtitle - only shown when dropdown is closed */
  readonly displaySubtitle = computed(() => {
    if (!this.isFinalSelection()) {
      return '';
    }

    return this.finalDisplaySubtitle();
  });

  /** CSS classes for the host element */
  protected readonly hostClasses = computed(() => ({
    'hx-cascading-search': true,
    'hx-cascading-search--disabled': this.disabled(),
    'hx-cascading-search--required': this.required(),
    'hx-cascading-search--has-value': this.selectionPath().length > 0,
  }));

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /** Handle option selection from dropdown */
  onOptionSelected(selectedOption: DropdownDisplayOption | DropdownDisplayOption[] | null): void {
    // Handle array case (shouldn't happen with single select, but being safe)
    if (Array.isArray(selectedOption)) {
      selectedOption = selectedOption[0] || null;
    }

    if (!selectedOption || this.disabled()) {
      return;
    }

    // Handle search result selection - set the full path
    if (selectedOption.isSearchResult && selectedOption.searchResultPath) {
      // Clear search query
      this.searchQuery.set('');

      // Set the full path from the search result
      const fullPath = selectedOption.searchResultPath;
      this.selectionPath.set(fullPath);

      // Check if this has children to navigate deeper
      const hasChildren = selectedOption.children && selectedOption.children.length > 0;
      const config = this.config();
      const isAtMaxDepth = config?.maxDepth && fullPath.length >= config.maxDepth;

      if (hasChildren && !isAtMaxDepth) {
        // Navigate to children - keep dropdown open
        this.isFinalSelection.set(false);

        // Keep dropdown open for further navigation
        setTimeout(() => {
          if (!this.isDropdownOpen()) {
            this.openDropdown();
          }
        }, 0);
      } else {
        // This is the final selection - close dropdown
        const separator = this.config().pathSeparator || ' > ';
        const pathText = fullPath.map((item) => item.label).join(separator);
        const pathSubtitle = fullPath
          .map((item) => item.subtitle || '')
          .filter(Boolean)
          .join(separator);
        this.finalDisplayValue.set(pathText);
        this.finalDisplaySubtitle.set(pathSubtitle);
        this.isFinalSelection.set(true);
        this.closeDropdown();
        this.emitSelection(fullPath);
        this.notifyValueChange(fullPath);
      }
      return;
    }

    // Handle path navigation click - navigate back one level
    if (selectedOption.isBreadcrumb && selectedOption.value) {
      // Navigate to parent level (one level up from current)
      const currentPath = this.selectionPath();
      if (currentPath.length > 1) {
        this.navigateToLevel(currentPath.length - 2);
      } else if (currentPath.length === 1) {
        // Go back to root
        this.selectionPath.set([]);
        this.isFinalSelection.set(false);
        this.finalDisplayValue.set('');
        this.finalDisplaySubtitle.set('');
        this.emitSelection([]);
        this.notifyValueChange([]);
      }
      // Keep dropdown open for navigation
      setTimeout(() => {
        this.openDropdown();
      }, 0);
      return;
    }

    // Handle separator (do nothing)
    if (selectedOption.isSeparator) {
      return;
    }

    // Handle regular option selection
    this.selectOption(selectedOption as CascadeOption);
  }

  /** Handle search query changes */
  onSearchChange(query: string): void {
    this.searchQuery.set(query);
  }

  /** Handle dropdown opened event */
  onDropdownOpened(): void {
    this.isDropdownOpen.set(true);
  }

  /** Handle dropdown closed event */
  onDropdownClosed(): void {
    this.isDropdownOpen.set(false);

    // When dropdown closes by clicking outside (not after final selection via selectOption),
    // emit the current path and mark as final selection
    const path = this.selectionPath();
    if (path.length > 0 && !this.isFinalSelection()) {
      const separator = this.config().pathSeparator || ' > ';
      const pathText = path.map((item) => item.label).join(separator);
      const pathSubtitle = path
        .map((item) => item.subtitle || '')
        .filter(Boolean)
        .join(separator);
      this.finalDisplayValue.set(pathText);
      this.finalDisplaySubtitle.set(pathSubtitle);
      this.isFinalSelection.set(true);

      // Emit selection to parent component
      this.emitSelection(path);
      this.notifyValueChange(path);
    }
  }

  /** Toggle dropdown state */
  toggleDropdown(): void {
    if (this.disabled()) {
      return;
    }

    const dropdown = this.dropdownComponent();
    if (dropdown) {
      if (this.isDropdownOpen()) {
        dropdown.close();
      } else {
        dropdown.open();
      }
    }
  }

  /** Open dropdown */
  openDropdown(): void {
    if (this.disabled()) {
      return;
    }

    const dropdown = this.dropdownComponent();
    if (dropdown) {
      dropdown.open();
    }
  }

  /** Close dropdown */
  closeDropdown(): void {
    const dropdown = this.dropdownComponent();
    if (dropdown) {
      dropdown.close();
    }
  }

  // ============================================================================
  // NAVIGATION METHODS
  // ============================================================================

  /** Select an option and navigate to its children or complete selection */
  private selectOption(option: CascadeOption): void {
    const currentPath = this.selectionPath();
    const newPath = [...currentPath, option];
    this.selectionPath.set(newPath);

    // Check if this has children and we can navigate deeper
    const hasChildren = option.children && option.children.length > 0;
    const config = this.config();
    const isAtMaxDepth = config?.maxDepth && newPath.length >= config.maxDepth;

    if (hasChildren && !isAtMaxDepth) {
      // Navigate to children - keep dropdown open
      this.isFinalSelection.set(false);

      // Emit intermediate selection to parent
      this.emitSelection(newPath);
      this.notifyValueChange(newPath);

      // Force the dropdown to stay open by reopening if it closed
      setTimeout(() => {
        if (!this.isDropdownOpen()) {
          this.openDropdown();
        }
      }, 0);
    } else {
      // This is the final selection
      const separator = this.config().pathSeparator || ' > ';
      const pathText = newPath.map((item) => item.label).join(separator);
      const pathSubtitle = newPath
        .map((item) => item.subtitle || '')
        .filter(Boolean)
        .join(separator);
      this.finalDisplayValue.set(pathText);
      this.finalDisplaySubtitle.set(pathSubtitle);
      this.isFinalSelection.set(true);

      // Close dropdown and emit the selection
      this.closeDropdown();
      this.emitSelection(newPath);
      this.notifyValueChange(newPath);
    }
  }

  /** Navigate back to parent level */
  private navigateToParent(): void {
    const currentPath = this.selectionPath();
    if (currentPath.length > 0) {
      this.selectionPath.set(currentPath.slice(0, -1));
      this.isFinalSelection.set(false);
      this.finalDisplayValue.set('');
      this.finalDisplaySubtitle.set('');
    }
  }

  /** Navigate to a specific level in the hierarchy */
  navigateToLevel(level: number): void {
    const currentPath = this.selectionPath();
    if (level >= 0 && level < currentPath.length) {
      const newPath = currentPath.slice(0, level + 1);
      this.selectionPath.set(newPath);
      this.isFinalSelection.set(false);
      this.finalDisplayValue.set('');
      this.finalDisplaySubtitle.set('');

      // Emit the updated selection to parent component
      this.emitSelection(newPath);

      // Notify form control of the change
      this.notifyValueChange(newPath);
    }
  }

  /** Emit the current selection state */
  private emitSelection(path: CascadeOption[]): void {
    const separator = this.config().pathSeparator || ' > ';
    const pathText = path.map((item) => item.label).join(separator);

    // Emit full selection object (for backwards compatibility)
    const selection: CascadeSelection = {
      path,
      finalSelection: path.length > 0 ? (path[path.length - 1] ?? null) : null,
      pathText,
    };
    this.cascadeUpdated.emit(selection);

    // Emit simplified path selection (recommended for new implementations)
    const pathSelection: CascadePathSelection = {
      path,
      pathText,
    };
    this.pathSelected.emit(pathSelection);
  }

  /** Notify form control of value changes */
  private notifyValueChange(path: CascadeOption[]): void {
    const separator = this.config().pathSeparator || ' > ';
    const pathText = path.map((item) => item.label).join(separator);

    const selection: CascadeSelection = {
      path,
      finalSelection: path.length > 0 ? (path[path.length - 1] ?? null) : null,
      pathText,
    };

    // Call ControlValueAccessor callbacks
    this._onChange(selection);
    this._onTouched();
  }

  // ============================================================================
  // CONTROL VALUE ACCESSOR
  // ============================================================================

  writeValue(value: CascadeSelection | null): void {
    if (value?.path) {
      this.selectionPath.set(value.path);
      if (value.finalSelection) {
        const separator = this.config().pathSeparator || ' > ';
        const pathSubtitle = value.path
          .map((item) => item.subtitle || '')
          .filter(Boolean)
          .join(separator);
        this.finalDisplayValue.set(value.pathText);
        this.finalDisplaySubtitle.set(pathSubtitle);
        this.isFinalSelection.set(true);
      }
    } else {
      this.selectionPath.set([]);
      this.isFinalSelection.set(false);
      this.finalDisplayValue.set('');
      this.finalDisplaySubtitle.set('');
    }
  }
}
